// i18n fragment - admin communications surfaces: Settings, Integrations,
// Business profile, and the Email triage views (list / message / endpoint
// editor). Populated by the Phase 6 sweep. See ../strings.ts.
import type { LocaleStrings } from './base';

export const adminCommsStrings: LocaleStrings = {
  en: {
    // ── web-ui SettingsView.vue (admin.settings.*) ──
    'admin.settings.title':                  'Workspace settings',
    'admin.settings.subtitle':               'Defaults that shape how agents behave across this workspace.',
    'admin.settings.save_failed':            'Could not save that change.',
    'admin.settings.tz_auto':                'Match each user',
    'admin.settings.tz_auto_hint':           "Use each recipient's own time zone",
    'admin.settings.lang_auto':              'Match the channel',
    'admin.settings.lang_en':                'English',
    'admin.settings.lang_sv':                'Swedish',
    'admin.settings.default_lang.title':     'Language',
    'admin.settings.default_lang.lede':      "The workspace's default interface language. Members who haven't set their own (for example a workspace with no connected Slack/Teams profiles) see this; it falls back to English.",
    'admin.settings.default_lang.label':     'Default language',
    'admin.settings.default_lang.aria':      'Workspace default language',
    'admin.settings.lang_nb':                'Norwegian',
    'admin.settings.lang_da':                'Danish',
    'admin.settings.lang_fi':                'Finnish',
    'admin.settings.lang_de':                'German',
    'admin.settings.lang_fr':                'French',
    'admin.settings.lang_es':                'Spanish',
    'admin.settings.lang_nl':                'Dutch',
    'admin.settings.lang_it':                'Italian',
    'admin.settings.lang_pt':                'Portuguese',
    'admin.settings.handle.title':           'Workspace URL',
    'admin.settings.handle.lede':            'The handle in your workspace address and the local part of your agents\' email. Renaming re-bases everyone onto the new URL.',
    'admin.settings.handle.change':          'Change URL',
    'admin.settings.handle.modal_title':     'Change workspace URL',
    'admin.settings.handle.modal_lede':      'This renames the handle in everyone\'s workspace address and your agents\' email. Existing links to the old URL and the old email address will stop working. Tell your team before you change it.',
    'admin.settings.handle.modal_save':      'Change URL',
    'admin.settings.signin.title':           'Sign-in access',
    'admin.settings.signin.lede_before':     'Anyone in your {source} workspace can sign in when access is',
    'admin.settings.signin.open':            'Open',
    'admin.settings.signin.lede_mid':        '. Set it to',
    'admin.settings.signin.approval':        'Approval required',
    'admin.settings.signin.lede_under':      'to hold new sign-ins for review under',
    'admin.settings.signin.users_link':      'Users',
    'admin.settings.signin.lede_after':      'Existing members are unaffected.',
    'admin.settings.signin.open_hint':       'Members sign in immediately.',
    'admin.settings.signin.approval_hint':   'New sign-ins wait for an admin to approve them.',
    'admin.settings.signin.guests_aria':     'Allow guest sign-in',
    'admin.settings.signin.guests_on':       'Guests outside your workspace can be invited to sign in.',
    'admin.settings.signin.guests_off':      'Only members of your workspace can sign in.',
    'admin.settings.persona.title':          'Persona distillation',
    'admin.settings.persona.lede':           'Lets agents learn a working profile of each user from their {source} activity, so replies fit how that person works.',
    'admin.settings.persona.on_hint':        'Agents build and use per-user profiles.',
    'admin.settings.persona.off_hint':       'Agents do not build per-user profiles.',
    'admin.settings.channel.title':          'Channel posting',
    'admin.settings.channel.lede_before':    'Lets agents post in your channels: a one-time',
    'admin.settings.channel.welcome':        'welcome',
    'admin.settings.channel.lede_mid':       'when someone first meets an agent, plus optional recurring',
    'admin.settings.channel.recommendations':'recommendations',
    'admin.settings.channel.lede_after':     'about skills and integrations they could use.',
    'admin.settings.channel.welcome_aria':   'Channel welcome message',
    'admin.settings.channel.welcome_label':  'Welcome message.',
    'admin.settings.channel.welcome_on':     'Agents greet users the first time they meet.',
    'admin.settings.channel.welcome_off':    'Agents stay quiet until spoken to.',
    'admin.settings.channel.recs_aria':      'Recurring recommendations',
    'admin.settings.channel.recs_label':     'Recurring recommendations.',
    'admin.settings.channel.recs_on':        'Agents occasionally suggest skills and integrations.',
    'admin.settings.channel.recs_off':       'No recurring suggestions.',
    'admin.settings.schedule.title':         'Recommendation schedule',
    'admin.settings.schedule.lede':          'When recurring recommendations may be sent, and the language to fall back to when a recipient\'s own is unknown.',
    'admin.settings.schedule.timezone':      'Time zone',
    'admin.settings.schedule.timezone_aria': 'Recommendation time zone',
    'admin.settings.schedule.start':         'From',
    'admin.settings.schedule.start_aria':    'Earliest hour',
    'admin.settings.schedule.end':           'Until',
    'admin.settings.schedule.end_aria':      'Latest hour',
    'admin.settings.schedule.fallback_lang': 'Fallback language',
    'admin.settings.schedule.fallback_lang_aria': 'Fallback language',
    'admin.settings.consent.title':          'Memory consent',
    'admin.settings.consent.admin_only':     'Admin only',
    'admin.settings.consent.lede_admin':     'you decide once for the whole workspace.',
    'admin.settings.consent.per_user':       'Per user',
    'admin.settings.consent.lede_per_user':  'each member chooses for themselves.',
    'admin.settings.consent.admin_hint':     'You set memory on or off for everyone.',
    'admin.settings.consent.per_user_hint':  'Each member opts in or out on their own.',
    'admin.settings.corpus.title':           'History window',
    'admin.settings.corpus.lede':            'How far back agents draw on your {source} history for context. Older messages stay where they are; this only sets the recall window.',
    'admin.settings.corpus.days':            'days',

    // ── web-ui IntegrationsView.vue (admin.integrations.*) ──
    'admin.integrations.title':              'Integrations',
    'admin.integrations.view_aria':          'Integration view',
    'admin.integrations.tab_builtin':        'Built-in',
    'admin.integrations.tab_system':         'System',
    'admin.integrations.tab_custom':         'Custom',
    'admin.integrations.tab_discover':       'Discover',
    'admin.integrations.custom_empty_title': 'No custom integrations yet',
    'admin.integrations.custom_empty_body':  'Connect your own MCP server to give agents tools the catalog does not cover.',
    'admin.integrations.connectors_tip':     'Manage workspace history connectors',
    'admin.integrations.connectors':         'Connectors',
    'admin.integrations.policy_tip':         'Set the policy for custom integrations',
    'admin.integrations.policy':             'Policy',
    'admin.integrations.add_custom_tip':     'Add a custom integration',
    'admin.integrations.add_custom_off_tip': 'Custom integrations are turned off for this workspace.',
    'admin.integrations.add_custom':         'Add custom',
    // Discover tab - read-only search of the wild public MCP ecosystem.
    'admin.integrations.discover.caption':            'Public MCP servers from the open ecosystem. HQ does not run or vet these. Adding one is a custom integration that needs setup and may not work.',
    'admin.integrations.discover.search_placeholder': 'Search the public MCP ecosystem (e.g. "stripe", "postgres")',
    'admin.integrations.discover.search_aria':        'Search the public MCP ecosystem',
    'admin.integrations.discover.prompt_title':       'Search the public MCP ecosystem',
    'admin.integrations.discover.prompt_body':        'Thousands of public MCP servers, refreshed daily. Type at least 2 characters to find one, then add the ones you trust as a custom integration.',
    'admin.integrations.discover.prompt_title_n':     'Search {total} public MCP servers',
    'admin.integrations.discover.prompt_body_n':      '{live} are live and ready to connect right now, refreshed daily. Type at least 2 characters to find one, then add the ones you trust as a custom integration.',
    'admin.integrations.discover.empty_title':        'No matching servers',
    'admin.integrations.discover.empty_body':         'Nothing in the public ecosystem matched. Try a different term.',
    'admin.integrations.discover.err':                'Search is unavailable right now.',
    'admin.integrations.discover.untitled':           'Untitled server',
    'admin.integrations.discover.tier_first_party':   'First-party',
    'admin.integrations.discover.tier_trusted':       'Trusted OSS',
    'admin.integrations.discover.tier_community':     'Community',
    'admin.integrations.discover.tier_filter_label':  'Source',
    'admin.integrations.discover.status_filter_label':'Status',
    'admin.integrations.discover.status_live':        'Live',
    'admin.integrations.discover.status_offline':     'Offline',
    'admin.integrations.discover.filtered_empty_title':'No matches for these filters',
    'admin.integrations.discover.filtered_empty_body': 'Try a different source or status, or clear the filters.',
    'admin.integrations.discover.tools_count':        '{count} tools',
    'admin.integrations.discover.live_ok':            'Live',
    'admin.integrations.discover.live_auth':          'Needs auth',
    'admin.integrations.discover.live_dead':          'Offline',
    'admin.integrations.discover.live_local':         'Local server',
    'admin.integrations.discover.caveat':             'Discovered in the public ecosystem - not installed, not vetted by HQ. Adding it requires setup and is not guaranteed to work.',
    'admin.integrations.discover.trust_label':        'Provenance',
    'admin.integrations.discover.stars':              '{count} stars',
    'admin.integrations.discover.reach_label':        'Reachability',
    'admin.integrations.discover.endpoint_label':     'Endpoint',
    'admin.integrations.discover.tools_label':        'What it can do',
    'admin.integrations.discover.tools_more':         '+{count} more',
    'admin.integrations.discover.view_repo':          'View repository',
    'admin.integrations.discover.view_home':          'Website',
    'admin.integrations.discover.add_tip':            'Try adding this as a custom integration',
    'admin.integrations.discover.add_to_workspace':   'Add to workspace',
    'admin.integrations.discover.not_addable':        'Not addable as a hosted integration',
    'admin.integrations.search_placeholder': 'Search integrations',
    'admin.integrations.search_aria':        'Search integrations',
    'admin.integrations.filter_all':         'All',
    'admin.integrations.no_results':         'No integrations match your filters',
    'admin.integrations.clear_filters':      'Clear filters',
    'admin.integrations.featured':           'Featured',
    'admin.integrations.featured_lede':      'Popular integrations, ready to connect.',
    'admin.integrations.system':             'System',
    'admin.integrations.system_lede':        'Always-on platform tools. No setup needed.',
    'admin.integrations.no_auth':            'Available now',
    'admin.integrations.no_auth_lede':       'One click to turn on. No credentials required.',
    'admin.integrations.auth':               'Requires authorization',
    'admin.integrations.auth_lede':          'Connect with OAuth or an API key before agents can use these.',
    'admin.integrations.attention':          'Needs attention',
    'admin.integrations.attention_lede':     'These stopped working or are waiting on a decision.',
    'admin.integrations.enable':             'Enable',
    'admin.integrations.disable':            'Disable',
    'admin.integrations.connect':            'Connect',
    'admin.integrations.reconnect':          'Reconnect',
    'admin.integrations.approve':            'Approve',
    'admin.integrations.approve_tip':        'Approve this custom integration for the workspace',
    'admin.integrations.remove_tip':         'Remove this custom integration',
    // detail side panel
    'admin.integrations.detail.tools':       'What it can do',
    'admin.integrations.detail.tools_count': '{count} actions',
    'admin.integrations.detail.tools_empty': 'The list of actions appears once this integration is connected.',
    'admin.integrations.detail.tools_loading':'Loading actions…',
    'admin.integrations.detail.publisher':   'Publisher',
    'admin.integrations.detail.publisher_hq':'HQ',
    'admin.integrations.detail.publisher_custom':'Added by your workspace',
    'admin.integrations.detail.connection':  'Connection',
    'admin.integrations.detail.conn_none':   'No connection needed',
    'admin.integrations.detail.conn_key':    'Connect with an API key',
    'admin.integrations.detail.conn_oauth':  'Connect by signing in',
    'admin.integrations.detail.version':     'Version',
    'admin.integrations.detail.always_on':   'Always on',
    'admin.integrations.detail.always_on_note':'Built in to the platform and always available.',
    'admin.integrations.detail.beta_unverified':'During our beta, {name} shows an "unverified app" notice on its sign-in screen while we finish its marketplace review. That is expected - go ahead and connect.',
    'admin.integrations.empty_title':        'No integrations yet',
    'admin.integrations.empty_body':         'The catalog is empty. Add a custom integration to get started.',
    'admin.integrations.approved':           'Approved {name}',
    'admin.integrations.connected':          'Connected {name}',
    'admin.integrations.remove_title':       'Remove {name}?',
    'admin.integrations.remove_body':        'This removes the custom integration from the workspace. Agents lose access to its tools.',
    'admin.integrations.removed':            'Removed {name}',
    'admin.integrations.status.reauth':      'Reconnect needed',
    'admin.integrations.status.failed':      'Failed',
    'admin.integrations.status.review':      'Awaiting review',
    'admin.integrations.status.paused':      'Paused',
    'admin.integrations.err.update':         'Could not update that integration.',
    'admin.integrations.err.approve':        'Could not approve that integration.',
    'admin.integrations.err.remove':         'Could not remove that integration.',
    'admin.integrations.err.oauth_start':    'Could not start the connection.',

    // ── web-ui BusinessProfileView.vue (admin.businessProfile.*) ──
    'admin.businessProfile.title':           'Business profile',
    'admin.businessProfile.subtitle':        "What your agents know about your company, and the brand they use for documents.",
    'admin.businessProfile.rerun_tip':       'Read your site again and refresh the profile',
    'admin.businessProfile.rerun':           'Re-read site',
    'admin.businessProfile.starting':        'Starting…',
    'admin.businessProfile.save_changes':    'Save changes',
    'admin.businessProfile.rerun_started':   'Reading your site now. This takes a moment.',
    'admin.businessProfile.nothing_changed': 'Nothing to save.',
    'admin.businessProfile.saved':           'Profile saved.',
    'admin.businessProfile.domain_invalid':  "That doesn't look like a website address.",
    'admin.businessProfile.website_saved':   'Website updated.',
    'admin.businessProfile.website_reread':  'Reading {domain} now.',
    'admin.businessProfile.reverted':        'Reverted to the detected value.',
    'admin.businessProfile.logo_saved':      'Logo updated.',
    'admin.businessProfile.unreachable':     "We couldn't read your site: {error}",
    'admin.businessProfile.reading_now':     'Reading your site now…',
    'admin.businessProfile.updated_just_now':'Updated just now',
    'admin.businessProfile.updated_min':     'Updated {count} min ago',
    'admin.businessProfile.updated_h':       'Updated {count} h ago',
    'admin.businessProfile.updated_d':       'Updated {count} d ago',
    'admin.businessProfile.updated_at':      'Updated {date}',
    'admin.businessProfile.profiled_at':     'Profiled {date}',
    'admin.businessProfile.website.title':   'Website',
    'admin.businessProfile.website.lede':    'The address your agents read to learn about your company.',
    'admin.businessProfile.website.none':    'No website set',
    'admin.businessProfile.website.change':  'Change',
    'admin.businessProfile.website.set':     'Set website',
    'admin.businessProfile.about.title':     'About the company',
    'admin.businessProfile.about.reset_tip': 'Revert to the detected summary',
    'admin.businessProfile.about.reset':     'Reset',
    'admin.businessProfile.about.lede':      'A short description agents read before every conversation. Edit to pin it.',
    'admin.businessProfile.about.placeholder':'Describe what your company does, who it serves, and how it sounds.',
    'admin.businessProfile.colours.title':   'Brand colours',
    'admin.businessProfile.colours.reset_tip':'Revert to the detected palette',
    'admin.businessProfile.colours.reset':   'Reset',
    'admin.businessProfile.colours.lede':    'Used when agents produce branded documents.',
    'admin.businessProfile.colours.slot_primary':   'Primary',
    'admin.businessProfile.colours.slot_secondary': 'Secondary',
    'admin.businessProfile.colours.slot_accent':    'Accent',
    'admin.businessProfile.colours.slot_background':'Background',
    'admin.businessProfile.colours.slot_text':      'Text',
    'admin.businessProfile.colours.slot_aria':      '{slot} colour',
    'admin.businessProfile.logo.title':      'Logo',
    'admin.businessProfile.logo.reset_tip':  'Revert to the detected logo',
    'admin.businessProfile.logo.reset':      'Reset',
    'admin.businessProfile.logo.lede':       'Used on branded documents agents create.',
    'admin.businessProfile.logo.preview_alt':'Logo preview',
    'admin.businessProfile.logo.managed':    'HQ is hosting this logo for your workspace. Upload a new file to replace it.',
    'admin.businessProfile.logo.uploading':  'Uploading…',
    'admin.businessProfile.logo.upload':     'Upload',
    'admin.businessProfile.fonts.title':     'Fonts',
    'admin.businessProfile.fonts.lede':      'Typefaces detected on your site.',
    'admin.businessProfile.intel.title':     'Company intelligence',
    'admin.businessProfile.intel.lede':      'What HQ inferred about your company from public signals.',
    'admin.businessProfile.intel.industry':  'Industry',
    'admin.businessProfile.intel.segment':   'Segment',
    'admin.businessProfile.intel.size':      'Size',
    'admin.businessProfile.intel.not_profiled':'Not profiled yet.',
    'admin.businessProfile.tech.title':      'Detected technology',
    'admin.businessProfile.tech.lede':       'Email, DNS, and web technology HQ found for your domain.',
    'admin.businessProfile.tech.email_dns':  'Email and DNS',
    'admin.businessProfile.tech.website':    'Website',
    'admin.businessProfile.tech.none':       'Nothing detected yet.',
    'admin.businessProfile.tech.connect_link':'Connect more sources',
    'admin.businessProfile.modal.title':     'Change website',
    'admin.businessProfile.modal.lede':      "Enter your company's website. HQ reads it to refresh the profile.",
    'admin.businessProfile.modal.save':      'Save and re-read',
    'admin.businessProfile.err.load':        'Could not load the business profile.',
    'admin.businessProfile.err.rerun':       'Could not start the re-read.',
    'admin.businessProfile.err.save':        'Could not save the profile.',
    'admin.businessProfile.err.revert':      'Could not revert that field.',
    'admin.businessProfile.err.upload':      'Could not upload the logo.',

    // ── web-ui EmailView.vue (admin.email.*) ──
    'admin.email.title':                     'Email',
    'admin.email.view_aria':                 'Email view',
    'admin.email.tab_review':                'Review',
    'admin.email.tab_review_count':          'Review ({count})',
    'admin.email.tab_threads':               'Threads',
    'admin.email.tab_activity':              'Activity',
    'admin.email.tab_endpoints':             'Inboxes',
    'admin.email.new_inbox':                 'New inbox',
    'admin.email.unassigned':                'Unassigned',
    'admin.email.agent_fallback':            'an agent',
    'admin.email.to':                        'To {addrs}',
    'admin.email.no_subject':                '(no subject)',
    'admin.email.no_sender':                 '(no sender)',
    'admin.email.open_thread':               'Open thread',
    'admin.email.reply':                     'Reply',
    'admin.email.send_reply':                'Send reply',
    'admin.email.reply_placeholder':         'Write a reply…',
    'admin.email.discard':                   'Discard',
    'admin.email.accept':                    'Accept',
    'admin.email.reject':                    'Reject',
    'admin.email.assign_retry':              'Assign and retry',
    'admin.email.choose_agent':              'Choose an agent',
    'admin.email.assign_aria':               'Assign to agent',
    'admin.email.bind_placeholder':          'Bind to a user',
    'admin.email.bind_aria':                 'Bind sender to user',
    'admin.email.inboxes':                   'Inboxes',
    'admin.email.inbox_empty_title':         'No custom inboxes yet',
    'admin.email.inbox_empty_body':          'Create an inbox to route mail to a specific agent.',
    'admin.email.edit_inbox_tip':            'Edit inbox',
    'admin.email.total_count':               '{count} total',
    'admin.email.to_review_count':           '{count} to review',
    'admin.email.thread_count_one':          '{count} thread',
    'admin.email.thread_count_other':        '{count} threads',
    'admin.email.message_count_one':         '{count} message',
    'admin.email.message_count_other':       '{count} messages',
    'admin.email.file_count_one':            '{count} file',
    'admin.email.file_count_other':          '{count} files',
    'admin.email.sort_threads_aria':         'Sort threads',
    'admin.email.sort_activity_aria':        'Sort activity',
    'admin.email.dir_in':                    'In',
    'admin.email.dir_out':                   'Out',
    'admin.email.reply_sent':                'Reply sent.',
    'admin.email.draft_discarded':           'Draft discarded.',
    'admin.email.assigned':                  'Assigned.',
    'admin.email.accepted':                  'Accepted.',
    'admin.email.rejected':                  'Rejected.',
    'admin.email.discard_title':             'Discard this draft?',
    'admin.email.discard_body':              'The drafted reply will not be sent. The thread stays in your history.',
    'admin.email.reject_title':              'Reject this mail?',
    'admin.email.reject_body':               'Mail from {from} will be dismissed. This does not delete it from the audit trail.',
    'admin.email.review.empty_title':        'All clear',
    'admin.email.review.empty_body':         'Nothing needs your attention right now.',
    'admin.email.review.awaiting_title':     'Awaiting review',
    'admin.email.review.awaiting_sub':       'Drafted replies an agent held for you to send or discard.',
    'admin.email.review.unrouted_title':     'Unrouted',
    'admin.email.review.unrouted_sub':       'Mail with no handler. Assign an agent to route it.',
    'admin.email.review.rr_title':           'Replies to your team address',
    'admin.email.review.rr_sub':             'Replies to mail the assistant sent from the team address. For a human to read - never fed back to the assistant. Assign unowned ones to a teammate.',
    'admin.email.review.rr_unassigned':      'Unassigned',
    'admin.email.review.rr_assign_placeholder': 'Assign to...',
    'admin.email.review.rr_assign_aria':     'Assign this reply to a user',
    'admin.email.review.rr_assign':          'Assign',
    'admin.email.review.rr_done_btn':        'Done',
    'admin.email.review.rr_assigned':        'Reply assigned.',
    'admin.email.review.rr_done':            'Marked reviewed.',
    'admin.email.review.err_pick_user':      'Pick a user first.',
    'admin.email.review.err_assign':         'Could not assign the reply.',
    'admin.email.review.err_done':           'Could not mark it reviewed.',
    'admin.email.review.quar_title':         'Quarantined',
    'admin.email.review.quar_sub':           'Held at the sender gate. Accept to let it through, or reject it.',
    'admin.email.threads.empty_title':       'No email threads yet',
    'admin.email.threads.empty_body':        'Conversations started by email show up here.',
    'admin.email.activity.empty_title':      'No activity yet',
    'admin.email.activity.empty_body':       'Inbound and outbound mail shows up here as it happens.',
    'admin.email.mb.title':                  'Workspace mailbox',
    'admin.email.mb.sub':                    'The address agents send from, and where replies land. Managed by HQ.',
    'admin.email.mb.change_address':         'Change address',
    'admin.email.mb.address':                'Address',
    'admin.email.mb.hint':                   'Lowercase letters, digits, and . / - only.',
    'admin.email.mb.updated':                'Workspace mailbox updated.',
    'admin.email.mb.err_shape':              "That doesn't look like a valid address.",
    'admin.email.mb.err_taken':              'That address is already in use.',
    'admin.email.mb.err_update':             'Could not update the mailbox.',
    'admin.email.mb.court.title':            'Court document intake (Sweden)',
    'admin.email.mb.court.sub':              'When a Swedish court sends documents via "Säker e-post", download them into the library automatically. The email still appears in your review queue.',
    'admin.email.mb.court.configure':        'Configure',
    'admin.email.mb.court.on':               'On',
    'admin.email.mb.court.off':              'Off',
    'admin.email.mb.court.pw_set':           'Secure-mail password stored',
    'admin.email.mb.court.enable':           'Download court documents automatically',
    'admin.email.mb.court.password':         'Secure-mail password',
    'admin.email.mb.court.pw_ph':            'Password for the court secure-mail portal',
    'admin.email.mb.court.pw_keep':          'Leave blank to keep the stored password',
    'admin.email.mb.court.hint':             'Used to open the court "Säker e-post" message. Stored encrypted; never shown again.',
    'admin.email.mb.court.saved':            'Court intake settings saved.',
    'admin.email.mb.court.err':              'Could not save court intake settings.',
    'admin.email.reply.agent_decides':       'Agent decides',
    'admin.email.reply.always':              'Always reply',
    'admin.email.reply.never':               'Never reply',
    'admin.email.reply.human_review':        'Draft for human review',
    'admin.email.sender.anyone':             'Anyone',
    'admin.email.sender.bound':              'Bound users only',
    'admin.email.sender.domain':             'Domain allowlist',
    'admin.email.sender.addr':               'Address allowlist',
    'admin.email.answered.files':            'Files only',
    'admin.email.answered.quick':            'Quick reply',
    'admin.email.state.handled':             'Handled',
    'admin.email.state.awaiting':            'Awaiting review',
    'admin.email.state.unrouted':            'Unrouted',
    'admin.email.reason.tenant_unknown':     'Unknown workspace',
    'admin.email.reason.alias_expired':      'Alias expired',
    'admin.email.reason.agent_unknown':      'Unknown agent',
    'admin.email.reason.agent_disabled':     'Agent disabled',
    'admin.email.reason.unknown_sender':     'Unknown sender',
    'admin.email.reason.spam':               'Spam threshold',
    'admin.email.reason.malformed':          'Malformed address',
    'admin.email.reason.category_blocked':   'Category blocked',
    'admin.email.reason.rate_limited':       'Rate limited',
    'admin.email.sort.newest':               'Newest',
    'admin.email.sort.oldest':               'Oldest',
    'admin.email.sort.title_az':             'Title A-Z',
    'admin.email.metric.received':           'received',
    'admin.email.metric.replied':            'replied',
    'admin.email.metric.escalated':          'escalated',
    'admin.email.metric.awaiting':           'awaiting',
    'admin.email.metric.unrouted':           'unrouted',
    'admin.email.err.write_reply':           'Write a reply first.',
    'admin.email.err.send':                  'Could not send the reply.',
    'admin.email.err.pick_agent':            'Pick an agent first.',
    'admin.email.err.assign':                'Could not assign the message.',
    'admin.email.err.accept':                'Could not accept the message.',

    // ── web-ui EmailMessageView.vue (admin.emailMessage.*) ──
    'admin.emailMessage.title_message':      'Email message',
    'admin.emailMessage.title_quarantine':   'Quarantined message',
    'admin.emailMessage.lens_text':          'Text',
    'admin.emailMessage.lens_html':          'HTML',
    'admin.emailMessage.reject':             'Reject',
    'admin.emailMessage.accept':             'Accept',
    'admin.emailMessage.assign_retry':       'Assign and retry',
    'admin.emailMessage.open_thread':        'Open thread',
    'admin.emailMessage.discard':            'Discard',
    'admin.emailMessage.done':               'Done',
    'admin.emailMessage.done_toast':         'Marked reviewed.',
    'admin.emailMessage.reopen':             'Reopen',
    'admin.emailMessage.reopen_toast':       'Moved back to review.',
    'admin.emailMessage.quar_notice':        'Held at the sender gate. Reading this does not release it.',
    'admin.emailMessage.meta_date':          'Date',
    'admin.emailMessage.meta_from':          'From',
    'admin.emailMessage.meta_to':            'To',
    'admin.emailMessage.meta_cc':            'Cc',
    'admin.emailMessage.meta_subject':       'Subject',
    'admin.emailMessage.body_format_aria':   'Body format',
    'admin.emailMessage.bind_label':         'Bind sender to a user (optional)',
    'admin.emailMessage.choose_user':        'Choose a user…',
    'admin.emailMessage.bind_aria':          'Bind sender to user',
    'admin.emailMessage.assign_label':       'Assign to an agent',
    'admin.emailMessage.choose_agent':       'Choose an agent…',
    'admin.emailMessage.assign_aria':        'Assign to agent',
    'admin.emailMessage.html_body_title':    'Email HTML body',
    'admin.emailMessage.no_body':            'This message has no readable body.',
    'admin.emailMessage.attachment_count_one':'{count} attachment',
    'admin.emailMessage.attachment_count_other':'{count} attachments',
    'admin.emailMessage.attachment_fallback':'attachment',
    'admin.emailMessage.drag_resize':        'Drag to resize',
    'admin.emailMessage.download':           'Download',
    'admin.emailMessage.close_preview':      'Close preview',
    'admin.emailMessage.this_sender':        'this sender',
    'admin.emailMessage.accepted':           'Accepted.',
    'admin.emailMessage.rejected':           'Rejected.',
    'admin.emailMessage.draft_discarded':    'Draft discarded.',
    'admin.emailMessage.assigned':           'Assigned - the agent is handling it now.',
    'admin.emailMessage.reject_title':       'Reject this mail?',
    'admin.emailMessage.reject_body':        'Mail from {from} will be dismissed. This does not delete it from the audit trail.',
    'admin.emailMessage.discard_title':      'Discard this draft?',
    'admin.emailMessage.discard_body':       'The drafted reply will not be sent. The thread stays in your history.',
    'admin.emailMessage.err.load':           'Could not load this message.',
    'admin.emailMessage.err.generic':        'Something went wrong.',

    // ── web-ui EmailEndpointEditorView.vue (admin.emailEndpoint.*) ──
    'admin.emailEndpoint.title_new':         'New email endpoint',
    'admin.emailEndpoint.title_edit':        'Edit {name}',
    'admin.emailEndpoint.endpoint_fallback': 'endpoint',
    'admin.emailEndpoint.enabled_aria':      'Inbox enabled',
    'admin.emailEndpoint.delete_inbox':      'Delete inbox',
    'admin.emailEndpoint.save_changes':      'Save changes',
    'admin.emailEndpoint.create_endpoint':   'Create endpoint',
    'admin.emailEndpoint.not_found':         'Endpoint not found.',
    'admin.emailEndpoint.plane_light':       'Quick reply',
    'admin.emailEndpoint.plane_agent':       'Agent',
    'admin.emailEndpoint.sender_bound':      'Bound users only',
    'admin.emailEndpoint.sender_domain':     'Domain allowlist',
    'admin.emailEndpoint.sender_addr':       'Address allowlist',
    'admin.emailEndpoint.sender_anyone':     'Anyone (public)',
    'admin.emailEndpoint.reply_agent_decides':'Agent decides',
    'admin.emailEndpoint.reply_always':      'Always reply',
    'admin.emailEndpoint.reply_never':       'Never reply',
    'admin.emailEndpoint.reply_human_review':'Draft for human review',
    'admin.emailEndpoint.address.title':     'Address',
    'admin.emailEndpoint.address.lede':      'The mail address your agents answer. Set once, then fixed.',
    'admin.emailEndpoint.address.label':     'Address',
    'admin.emailEndpoint.address.hint':      "the name before the dash; lowercase letters, digits, '.' / '-'",
    'admin.emailEndpoint.address.err_no_email':"Email isn't set up for this workspace yet - an admin needs to set its email address before adding endpoints.",
    'admin.emailEndpoint.address.err_taken': 'That address is already in use - pick another name.',
    'admin.emailEndpoint.address.full':      'Full address:',
    'admin.emailEndpoint.address.fixed':     'The address is fixed after creation.',
    'admin.emailEndpoint.replies.title':     'Replies',
    'admin.emailEndpoint.replies.lede':      'Whether this inbox answers mail at all.',
    'admin.emailEndpoint.replies.when_label':'When to reply',
    'admin.emailEndpoint.replies.never_hint':'This inbox never replies - mail is received, categorized, and filed only. No responder is needed.',
    'admin.emailEndpoint.who.title':         'Who answers',
    'admin.emailEndpoint.who.lede':          'A Quick reply is answered by HQ directly (no agent). Choose Agent to hand the thread to a specific agent instead.',
    'admin.emailEndpoint.who.mode_label':    'Reply mode',
    'admin.emailEndpoint.who.mode_hint_light':'Quick reply: HQ composes a fast answer with memory + document recall - no agent, no sandbox. Billed + metered.',
    'admin.emailEndpoint.who.mode_hint_agent':'Agent: a specific agent handles the thread with its full toolset (files, browser, integrations) in a sandbox.',
    'admin.emailEndpoint.who.agent_label':   'Agent',
    'admin.emailEndpoint.who.agent_hint_none':'No enabled agents - create one first.',
    'admin.emailEndpoint.who.agent_hint':    'The agent needs the Email integration enabled to send replies.',
    'admin.emailEndpoint.who.agent_placeholder':'Choose an agent…',
    'admin.emailEndpoint.who.instructions_label':'Instructions',
    'admin.emailEndpoint.who.instructions_hint':'role notes for this address (optional)',
    'admin.emailEndpoint.who.instructions_placeholder':'You handle inbound sales mail for ACME. Be concise; never quote pricing without confirming.',
    'admin.emailEndpoint.who.reply_lang_label':'Reply language',
    'admin.emailEndpoint.who.reply_lang_hint':"replies match the sender's language automatically; this is only the fallback for when it can't be detected",
    'admin.emailEndpoint.who.reply_lang_placeholder':'English (fallback)',
    'admin.emailEndpoint.access.title':      'Who can write in',
    'admin.emailEndpoint.access.lede':       'The sender gate. Mail from senders outside it is quarantined.',
    'admin.emailEndpoint.access.sender_label':'Sender policy',
    'admin.emailEndpoint.access.allowlist_label':'Allowlist',
    'admin.emailEndpoint.access.allowlist_hint_domains':'domains (Enter to add)',
    'admin.emailEndpoint.access.allowlist_hint_addresses':'addresses (Enter to add)',
    'admin.emailEndpoint.limits.title':      'Limits',
    'admin.emailEndpoint.limits.lede':       'Cost / abuse breakers. Blank = platform default.',
    'admin.emailEndpoint.limits.spam_label': 'Max spam score',
    'admin.emailEndpoint.limits.turns_sender_label':'Turns / sender / day',
    'admin.emailEndpoint.limits.turns_day_label':'Turns / day',
    'admin.emailEndpoint.updated':           'Endpoint updated.',
    'admin.emailEndpoint.created':           'Endpoint created.',
    'admin.emailEndpoint.deleted':           'Endpoint deleted.',
    'admin.emailEndpoint.delete_title':      'Delete this endpoint?',
    'admin.emailEndpoint.delete_body':       'Mail to {address} will no longer be answered. Existing threads are kept.',
    'admin.emailEndpoint.err.save':          'Could not save the endpoint.',
  },
  sv: {
    // ── web-ui SettingsView.vue (admin.settings.*) ──
    'admin.settings.title':                  'Arbetsyteinställningar',
    'admin.settings.subtitle':               'Standardval som formar hur agenter beter sig i den här arbetsytan.',
    'admin.settings.save_failed':            'Det gick inte att spara ändringen.',
    'admin.settings.tz_auto':                'Matcha varje användare',
    'admin.settings.tz_auto_hint':           'Använd varje mottagares egen tidszon',
    'admin.settings.lang_auto':              'Matcha kanalen',
    'admin.settings.lang_en':                'Engelska',
    'admin.settings.lang_sv':                'Svenska',
    'admin.settings.default_lang.title':     'Språk',
    'admin.settings.default_lang.lede':      'Arbetsytans förvalda gränssnittsspråk. Medlemmar som inte valt eget (till exempel en arbetsyta utan kopplade Slack-/Teams-profiler) ser detta; det faller tillbaka till engelska.',
    'admin.settings.default_lang.label':     'Förvalt språk',
    'admin.settings.default_lang.aria':      'Arbetsytans förvalda språk',
    'admin.settings.lang_nb':                'Norska',
    'admin.settings.lang_da':                'Danska',
    'admin.settings.lang_fi':                'Finska',
    'admin.settings.lang_de':                'Tyska',
    'admin.settings.lang_fr':                'Franska',
    'admin.settings.lang_es':                'Spanska',
    'admin.settings.lang_nl':                'Nederländska',
    'admin.settings.lang_it':                'Italienska',
    'admin.settings.lang_pt':                'Portugisiska',
    'admin.settings.handle.title':           'Arbetsytans URL',
    'admin.settings.handle.lede':            'Namnet i din arbetsyteadress och den lokala delen av dina agenters e-post. Byter du namn flyttas alla över till den nya URL:en.',
    'admin.settings.handle.change':          'Ändra URL',
    'admin.settings.handle.modal_title':     'Ändra arbetsytans URL',
    'admin.settings.handle.modal_lede':      'Detta byter namnet i allas arbetsyteadress och dina agenters e-post. Befintliga länkar till den gamla URL:en och den gamla e-postadressen slutar fungera. Säg till ditt team innan du ändrar.',
    'admin.settings.handle.modal_save':      'Ändra URL',
    'admin.settings.signin.title':           'Inloggningsåtkomst',
    'admin.settings.signin.lede_before':     'Alla i din {source}-arbetsyta kan logga in när åtkomsten är',
    'admin.settings.signin.open':            'Öppen',
    'admin.settings.signin.lede_mid':        '. Ställ in den på',
    'admin.settings.signin.approval':        'Godkännande krävs',
    'admin.settings.signin.lede_under':      'för att hålla nya inloggningar för granskning under',
    'admin.settings.signin.users_link':      'Användare',
    'admin.settings.signin.lede_after':      'Befintliga medlemmar påverkas inte.',
    'admin.settings.signin.open_hint':       'Medlemmar loggar in direkt.',
    'admin.settings.signin.approval_hint':   'Nya inloggningar väntar på att en administratör godkänner dem.',
    'admin.settings.signin.guests_aria':     'Tillåt gästinloggning',
    'admin.settings.signin.guests_on':       'Gäster utanför din arbetsyta kan bjudas in att logga in.',
    'admin.settings.signin.guests_off':      'Endast medlemmar i din arbetsyta kan logga in.',
    'admin.settings.persona.title':          'Persondistillering',
    'admin.settings.persona.lede':           'Låter agenter lära sig en arbetsprofil för varje användare utifrån deras {source}-aktivitet, så att svaren passar hur personen arbetar.',
    'admin.settings.persona.on_hint':        'Agenter bygger och använder profiler per användare.',
    'admin.settings.persona.off_hint':       'Agenter bygger inga profiler per användare.',
    'admin.settings.channel.title':          'Kanalinlägg',
    'admin.settings.channel.lede_before':    'Låter agenter posta i dina kanaler: ett engångs-',
    'admin.settings.channel.welcome':        'välkomstmeddelande',
    'admin.settings.channel.lede_mid':       'när någon möter en agent första gången, plus valfria återkommande',
    'admin.settings.channel.recommendations':'rekommendationer',
    'admin.settings.channel.lede_after':     'om färdigheter och integrationer de skulle kunna använda.',
    'admin.settings.channel.welcome_aria':   'Välkomstmeddelande i kanal',
    'admin.settings.channel.welcome_label':  'Välkomstmeddelande.',
    'admin.settings.channel.welcome_on':     'Agenter hälsar på användare första gången de möts.',
    'admin.settings.channel.welcome_off':    'Agenter är tysta tills de tilltalas.',
    'admin.settings.channel.recs_aria':      'Återkommande rekommendationer',
    'admin.settings.channel.recs_label':     'Återkommande rekommendationer.',
    'admin.settings.channel.recs_on':        'Agenter föreslår ibland färdigheter och integrationer.',
    'admin.settings.channel.recs_off':       'Inga återkommande förslag.',
    'admin.settings.schedule.title':         'Rekommendationsschema',
    'admin.settings.schedule.lede':          'När återkommande rekommendationer får skickas, och vilket språk som används när mottagarens eget är okänt.',
    'admin.settings.schedule.timezone':      'Tidszon',
    'admin.settings.schedule.timezone_aria': 'Tidszon för rekommendationer',
    'admin.settings.schedule.start':         'Från',
    'admin.settings.schedule.start_aria':    'Tidigaste timme',
    'admin.settings.schedule.end':           'Till',
    'admin.settings.schedule.end_aria':      'Senaste timme',
    'admin.settings.schedule.fallback_lang': 'Reservspråk',
    'admin.settings.schedule.fallback_lang_aria': 'Reservspråk',
    'admin.settings.consent.title':          'Minnessamtycke',
    'admin.settings.consent.admin_only':     'Endast administratör',
    'admin.settings.consent.lede_admin':     'du bestämmer en gång för hela arbetsytan.',
    'admin.settings.consent.per_user':       'Per användare',
    'admin.settings.consent.lede_per_user':  'varje medlem väljer själv.',
    'admin.settings.consent.admin_hint':     'Du slår på eller av minne för alla.',
    'admin.settings.consent.per_user_hint':  'Varje medlem väljer själv att delta eller inte.',
    'admin.settings.corpus.title':           'Historikfönster',
    'admin.settings.corpus.lede':            'Hur långt tillbaka agenter hämtar din {source}-historik för sammanhang. Äldre meddelanden stannar där de är; detta sätter bara hur långt minnet sträcker sig.',
    'admin.settings.corpus.days':            'dagar',

    // ── web-ui IntegrationsView.vue (admin.integrations.*) ──
    'admin.integrations.title':              'Integrationer',
    'admin.integrations.view_aria':          'Integrationsvy',
    'admin.integrations.tab_builtin':        'Inbyggda',
    'admin.integrations.tab_system':         'System',
    'admin.integrations.tab_custom':         'Anpassade',
    'admin.integrations.tab_discover':       'Upptäck',
    'admin.integrations.custom_empty_title': 'Inga anpassade integrationer än',
    'admin.integrations.custom_empty_body':  'Anslut din egen MCP-server för att ge agenter verktyg som katalogen inte täcker.',
    'admin.integrations.connectors_tip':     'Hantera anslutningar för arbetsytans historik',
    'admin.integrations.connectors':         'Anslutningar',
    'admin.integrations.policy_tip':         'Ställ in policyn för anpassade integrationer',
    'admin.integrations.policy':             'Policy',
    'admin.integrations.add_custom_tip':     'Lägg till en anpassad integration',
    'admin.integrations.add_custom_off_tip': 'Anpassade integrationer är avstängda för den här arbetsytan.',
    'admin.integrations.add_custom':         'Lägg till anpassad',
    // Discover-fliken - sökning i det vilda publika MCP-ekosystemet.
    'admin.integrations.discover.caption':            'Publika MCP-servrar från det öppna ekosystemet. HQ kör eller granskar inte dessa. Att lägga till en är en anpassad integration som kräver konfiguration och kanske inte fungerar.',
    'admin.integrations.discover.search_placeholder': 'Sök i det publika MCP-ekosystemet (t.ex. "stripe", "postgres")',
    'admin.integrations.discover.search_aria':        'Sök i det publika MCP-ekosystemet',
    'admin.integrations.discover.prompt_title':       'Sök i det publika MCP-ekosystemet',
    'admin.integrations.discover.prompt_body':        'Tusentals publika MCP-servrar, uppdateras dagligen. Skriv minst 2 tecken för att hitta en, lägg sedan till de du litar på som en anpassad integration.',
    'admin.integrations.discover.prompt_title_n':     'Sök bland {total} publika MCP-servrar',
    'admin.integrations.discover.prompt_body_n':      '{live} är aktiva och redo att anslutas just nu, uppdateras dagligen. Skriv minst 2 tecken för att hitta en, lägg sedan till de du litar på som en anpassad integration.',
    'admin.integrations.discover.empty_title':        'Inga matchande servrar',
    'admin.integrations.discover.empty_body':         'Inget i det publika ekosystemet matchade. Prova en annan sökterm.',
    'admin.integrations.discover.err':                'Sökningen är inte tillgänglig just nu.',
    'admin.integrations.discover.untitled':           'Namnlös server',
    'admin.integrations.discover.tier_first_party':   'Förstapart',
    'admin.integrations.discover.tier_trusted':       'Betrodd OSS',
    'admin.integrations.discover.tier_community':     'Community',
    'admin.integrations.discover.tier_filter_label':  'Källa',
    'admin.integrations.discover.status_filter_label':'Status',
    'admin.integrations.discover.status_live':        'Aktiv',
    'admin.integrations.discover.status_offline':     'Offline',
    'admin.integrations.discover.filtered_empty_title':'Inga träffar för dessa filter',
    'admin.integrations.discover.filtered_empty_body': 'Prova en annan källa eller status, eller rensa filtren.',
    'admin.integrations.discover.tools_count':        '{count} verktyg',
    'admin.integrations.discover.live_ok':            'Aktiv',
    'admin.integrations.discover.live_auth':          'Kräver autentisering',
    'admin.integrations.discover.live_dead':          'Offline',
    'admin.integrations.discover.live_local':         'Lokal server',
    'admin.integrations.discover.caveat':             'Upptäckt i det publika ekosystemet - inte installerad och inte granskad av HQ. Att lägga till den kräver konfiguration och fungerar inte garanterat.',
    'admin.integrations.discover.trust_label':        'Ursprung',
    'admin.integrations.discover.stars':              '{count} stjärnor',
    'admin.integrations.discover.reach_label':        'Tillgänglighet',
    'admin.integrations.discover.endpoint_label':     'Slutpunkt',
    'admin.integrations.discover.tools_label':        'Vad den kan göra',
    'admin.integrations.discover.tools_more':         '+{count} till',
    'admin.integrations.discover.view_repo':          'Visa kodförråd',
    'admin.integrations.discover.view_home':          'Webbplats',
    'admin.integrations.discover.add_tip':            'Försök lägga till som en anpassad integration',
    'admin.integrations.discover.add_to_workspace':   'Lägg till i arbetsytan',
    'admin.integrations.discover.not_addable':        'Kan inte läggas till som värdbaserad integration',
    'admin.integrations.search_placeholder': 'Sök integrationer',
    'admin.integrations.search_aria':        'Sök integrationer',
    'admin.integrations.filter_all':         'Alla',
    'admin.integrations.no_results':         'Inga integrationer matchar dina filter',
    'admin.integrations.clear_filters':      'Rensa filter',
    'admin.integrations.featured':           'Utvalda',
    'admin.integrations.featured_lede':      'Populära integrationer, redo att koppla in.',
    'admin.integrations.system':             'System',
    'admin.integrations.system_lede':        'Alltid aktiva plattformsverktyg. Ingen uppsättning behövs.',
    'admin.integrations.no_auth':            'Tillgängliga nu',
    'admin.integrations.no_auth_lede':       'Ett klick för att slå på. Inga inloggningsuppgifter krävs.',
    'admin.integrations.auth':               'Kräver behörighet',
    'admin.integrations.auth_lede':          'Anslut med OAuth eller en API-nyckel innan agenter kan använda dessa.',
    'admin.integrations.attention':          'Behöver åtgärd',
    'admin.integrations.attention_lede':     'Dessa slutade fungera eller väntar på ett beslut.',
    'admin.integrations.enable':             'Aktivera',
    'admin.integrations.disable':            'Inaktivera',
    'admin.integrations.connect':            'Anslut',
    'admin.integrations.reconnect':          'Återanslut',
    'admin.integrations.approve':            'Godkänn',
    'admin.integrations.approve_tip':        'Godkänn den här anpassade integrationen för arbetsytan',
    'admin.integrations.remove_tip':         'Ta bort den här anpassade integrationen',
    'admin.integrations.detail.tools':       'Vad den kan göra',
    'admin.integrations.detail.tools_count': '{count} åtgärder',
    'admin.integrations.detail.tools_empty': 'Listan med åtgärder visas när integrationen har anslutits.',
    'admin.integrations.detail.tools_loading':'Laddar åtgärder…',
    'admin.integrations.detail.publisher':   'Utgivare',
    'admin.integrations.detail.publisher_hq':'HQ',
    'admin.integrations.detail.publisher_custom':'Tillagd av din arbetsyta',
    'admin.integrations.detail.connection':  'Anslutning',
    'admin.integrations.detail.conn_none':   'Ingen anslutning behövs',
    'admin.integrations.detail.conn_key':    'Anslut med en API-nyckel',
    'admin.integrations.detail.conn_oauth':  'Anslut genom att logga in',
    'admin.integrations.detail.version':     'Version',
    'admin.integrations.detail.always_on':   'Alltid på',
    'admin.integrations.detail.always_on_note':'Inbyggd i plattformen och alltid tillgänglig.',
    'admin.integrations.detail.beta_unverified':'Under vår beta visar {name} en "overifierad app"-avisering på inloggningsskärmen medan vi slutför granskningen för marknadsplatsen. Det är förväntat - fortsätt och anslut.',
    'admin.integrations.empty_title':        'Inga integrationer än',
    'admin.integrations.empty_body':         'Katalogen är tom. Lägg till en anpassad integration för att komma igång.',
    'admin.integrations.approved':           'Godkände {name}',
    'admin.integrations.connected':          'Anslöt {name}',
    'admin.integrations.remove_title':       'Ta bort {name}?',
    'admin.integrations.remove_body':        'Detta tar bort den anpassade integrationen från arbetsytan. Agenter förlorar åtkomst till dess verktyg.',
    'admin.integrations.removed':            'Tog bort {name}',
    'admin.integrations.status.reauth':      'Återanslutning krävs',
    'admin.integrations.status.failed':      'Misslyckades',
    'admin.integrations.status.review':      'Väntar på granskning',
    'admin.integrations.status.paused':      'Pausad',
    'admin.integrations.err.update':         'Det gick inte att uppdatera integrationen.',
    'admin.integrations.err.approve':        'Det gick inte att godkänna integrationen.',
    'admin.integrations.err.remove':         'Det gick inte att ta bort integrationen.',
    'admin.integrations.err.oauth_start':    'Det gick inte att starta anslutningen.',

    // ── web-ui BusinessProfileView.vue (admin.businessProfile.*) ──
    'admin.businessProfile.title':           'Företagsprofil',
    'admin.businessProfile.subtitle':        'Vad dina agenter vet om ditt företag, och varumärket de använder för dokument.',
    'admin.businessProfile.rerun_tip':       'Läs din webbplats igen och uppdatera profilen',
    'admin.businessProfile.rerun':           'Läs om webbplatsen',
    'admin.businessProfile.starting':        'Startar…',
    'admin.businessProfile.save_changes':    'Spara ändringar',
    'admin.businessProfile.rerun_started':   'Läser din webbplats nu. Detta tar en stund.',
    'admin.businessProfile.nothing_changed': 'Inget att spara.',
    'admin.businessProfile.saved':           'Profilen sparad.',
    'admin.businessProfile.domain_invalid':  'Det ser inte ut som en webbadress.',
    'admin.businessProfile.website_saved':   'Webbplatsen uppdaterad.',
    'admin.businessProfile.website_reread':  'Läser {domain} nu.',
    'admin.businessProfile.reverted':        'Återställd till det upptäckta värdet.',
    'admin.businessProfile.logo_saved':      'Logotypen uppdaterad.',
    'admin.businessProfile.unreachable':     'Vi kunde inte läsa din webbplats: {error}',
    'admin.businessProfile.reading_now':     'Läser din webbplats nu…',
    'admin.businessProfile.updated_just_now':'Uppdaterad nyss',
    'admin.businessProfile.updated_min':     'Uppdaterad för {count} min sedan',
    'admin.businessProfile.updated_h':       'Uppdaterad för {count} tim sedan',
    'admin.businessProfile.updated_d':       'Uppdaterad för {count} d sedan',
    'admin.businessProfile.updated_at':      'Uppdaterad {date}',
    'admin.businessProfile.profiled_at':     'Profilerad {date}',
    'admin.businessProfile.website.title':   'Webbplats',
    'admin.businessProfile.website.lede':    'Adressen dina agenter läser för att lära sig om ditt företag.',
    'admin.businessProfile.website.none':    'Ingen webbplats angiven',
    'admin.businessProfile.website.change':  'Ändra',
    'admin.businessProfile.website.set':     'Ange webbplats',
    'admin.businessProfile.about.title':     'Om företaget',
    'admin.businessProfile.about.reset_tip': 'Återgå till den upptäckta sammanfattningen',
    'admin.businessProfile.about.reset':     'Återställ',
    'admin.businessProfile.about.lede':      'En kort beskrivning som agenter läser före varje konversation. Redigera för att fästa den.',
    'admin.businessProfile.about.placeholder':'Beskriv vad ditt företag gör, vem det betjänar och hur det låter.',
    'admin.businessProfile.colours.title':   'Varumärkesfärger',
    'admin.businessProfile.colours.reset_tip':'Återgå till den upptäckta paletten',
    'admin.businessProfile.colours.reset':   'Återställ',
    'admin.businessProfile.colours.lede':    'Används när agenter skapar varumärkta dokument.',
    'admin.businessProfile.colours.slot_primary':   'Primär',
    'admin.businessProfile.colours.slot_secondary': 'Sekundär',
    'admin.businessProfile.colours.slot_accent':    'Accent',
    'admin.businessProfile.colours.slot_background':'Bakgrund',
    'admin.businessProfile.colours.slot_text':      'Text',
    'admin.businessProfile.colours.slot_aria':      '{slot}-färg',
    'admin.businessProfile.logo.title':      'Logotyp',
    'admin.businessProfile.logo.reset_tip':  'Återgå till den upptäckta logotypen',
    'admin.businessProfile.logo.reset':      'Återställ',
    'admin.businessProfile.logo.lede':       'Används på varumärkta dokument som agenter skapar.',
    'admin.businessProfile.logo.preview_alt':'Förhandsvisning av logotyp',
    'admin.businessProfile.logo.managed':    'HQ är värd för den här logotypen åt din arbetsyta. Ladda upp en ny fil för att ersätta den.',
    'admin.businessProfile.logo.uploading':  'Laddar upp…',
    'admin.businessProfile.logo.upload':     'Ladda upp',
    'admin.businessProfile.fonts.title':     'Typsnitt',
    'admin.businessProfile.fonts.lede':      'Typsnitt som upptäckts på din webbplats.',
    'admin.businessProfile.intel.title':     'Företagsinsikter',
    'admin.businessProfile.intel.lede':      'Vad HQ härlett om ditt företag utifrån offentliga signaler.',
    'admin.businessProfile.intel.industry':  'Bransch',
    'admin.businessProfile.intel.segment':   'Segment',
    'admin.businessProfile.intel.size':      'Storlek',
    'admin.businessProfile.intel.not_profiled':'Inte profilerad än.',
    'admin.businessProfile.tech.title':      'Upptäckt teknik',
    'admin.businessProfile.tech.lede':       'E-post-, DNS- och webbteknik som HQ hittat för din domän.',
    'admin.businessProfile.tech.email_dns':  'E-post och DNS',
    'admin.businessProfile.tech.website':    'Webbplats',
    'admin.businessProfile.tech.none':       'Inget upptäckt än.',
    'admin.businessProfile.tech.connect_link':'Koppla in fler källor',
    'admin.businessProfile.modal.title':     'Ändra webbplats',
    'admin.businessProfile.modal.lede':      'Ange ditt företags webbplats. HQ läser den för att uppdatera profilen.',
    'admin.businessProfile.modal.save':      'Spara och läs om',
    'admin.businessProfile.err.load':        'Det gick inte att läsa in företagsprofilen.',
    'admin.businessProfile.err.rerun':       'Det gick inte att starta omläsningen.',
    'admin.businessProfile.err.save':        'Det gick inte att spara profilen.',
    'admin.businessProfile.err.revert':      'Det gick inte att återställa fältet.',
    'admin.businessProfile.err.upload':      'Det gick inte att ladda upp logotypen.',

    // ── web-ui EmailView.vue (admin.email.*) ──
    'admin.email.title':                     'E-post',
    'admin.email.view_aria':                 'E-postvy',
    'admin.email.tab_review':                'Granska',
    'admin.email.tab_review_count':          'Granska ({count})',
    'admin.email.tab_threads':               'Trådar',
    'admin.email.tab_activity':              'Aktivitet',
    'admin.email.tab_endpoints':             'Inkorgar',
    'admin.email.new_inbox':                 'Ny inkorg',
    'admin.email.unassigned':                'Otilldelad',
    'admin.email.agent_fallback':            'en agent',
    'admin.email.to':                        'Till {addrs}',
    'admin.email.no_subject':                '(inget ämne)',
    'admin.email.no_sender':                 '(ingen avsändare)',
    'admin.email.open_thread':               'Öppna tråd',
    'admin.email.reply':                     'Svara',
    'admin.email.send_reply':                'Skicka svar',
    'admin.email.reply_placeholder':         'Skriv ett svar…',
    'admin.email.discard':                   'Förkasta',
    'admin.email.accept':                    'Acceptera',
    'admin.email.reject':                    'Avvisa',
    'admin.email.assign_retry':              'Tilldela och försök igen',
    'admin.email.choose_agent':              'Välj en agent',
    'admin.email.assign_aria':               'Tilldela till agent',
    'admin.email.bind_placeholder':          'Bind till en användare',
    'admin.email.bind_aria':                 'Bind avsändare till användare',
    'admin.email.inboxes':                   'Inkorgar',
    'admin.email.inbox_empty_title':         'Inga anpassade inkorgar än',
    'admin.email.inbox_empty_body':          'Skapa en inkorg för att dirigera e-post till en specifik agent.',
    'admin.email.edit_inbox_tip':            'Redigera inkorg',
    'admin.email.total_count':               '{count} totalt',
    'admin.email.to_review_count':           '{count} att granska',
    'admin.email.thread_count_one':          '{count} tråd',
    'admin.email.thread_count_other':        '{count} trådar',
    'admin.email.message_count_one':         '{count} meddelande',
    'admin.email.message_count_other':       '{count} meddelanden',
    'admin.email.file_count_one':            '{count} fil',
    'admin.email.file_count_other':          '{count} filer',
    'admin.email.sort_threads_aria':         'Sortera trådar',
    'admin.email.sort_activity_aria':        'Sortera aktivitet',
    'admin.email.dir_in':                    'In',
    'admin.email.dir_out':                   'Ut',
    'admin.email.reply_sent':                'Svaret skickat.',
    'admin.email.draft_discarded':           'Utkastet förkastat.',
    'admin.email.assigned':                  'Tilldelad.',
    'admin.email.accepted':                  'Accepterad.',
    'admin.email.rejected':                  'Avvisad.',
    'admin.email.discard_title':             'Förkasta det här utkastet?',
    'admin.email.discard_body':              'Det utkastade svaret skickas inte. Tråden stannar i din historik.',
    'admin.email.reject_title':              'Avvisa det här mejlet?',
    'admin.email.reject_body':               'E-post från {from} kommer att avfärdas. Detta tar inte bort den från granskningsloggen.',
    'admin.email.review.empty_title':        'Allt klart',
    'admin.email.review.empty_body':         'Inget behöver din uppmärksamhet just nu.',
    'admin.email.review.awaiting_title':     'Väntar på granskning',
    'admin.email.review.awaiting_sub':       'Utkastade svar som en agent höll för att du ska skicka eller förkasta.',
    'admin.email.review.unrouted_title':     'Odirigerade',
    'admin.email.review.unrouted_sub':       'E-post utan hanterare. Tilldela en agent för att dirigera den.',
    'admin.email.review.rr_title':           'Svar till er teamadress',
    'admin.email.review.rr_sub':             'Svar på e-post som assistenten skickat från teamadressen. För en människa att läsa - matas aldrig tillbaka till assistenten. Tilldela de utan ägare till en kollega.',
    'admin.email.review.rr_unassigned':      'Otilldelad',
    'admin.email.review.rr_assign_placeholder': 'Tilldela till...',
    'admin.email.review.rr_assign_aria':     'Tilldela det här svaret till en användare',
    'admin.email.review.rr_assign':          'Tilldela',
    'admin.email.review.rr_done_btn':        'Klar',
    'admin.email.review.rr_assigned':        'Svaret tilldelat.',
    'admin.email.review.rr_done':            'Markerat som granskat.',
    'admin.email.review.err_pick_user':      'Välj en användare först.',
    'admin.email.review.err_assign':         'Kunde inte tilldela svaret.',
    'admin.email.review.err_done':           'Kunde inte markera som granskat.',
    'admin.email.review.quar_title':         'Karantän',
    'admin.email.review.quar_sub':           'Hålls vid avsändargrinden. Acceptera för att släppa igenom, eller avvisa den.',
    'admin.email.threads.empty_title':       'Inga e-posttrådar än',
    'admin.email.threads.empty_body':        'Konversationer som startats via e-post visas här.',
    'admin.email.activity.empty_title':      'Ingen aktivitet än',
    'admin.email.activity.empty_body':       'Inkommande och utgående e-post visas här när den händer.',
    'admin.email.mb.title':                  'Arbetsytans brevlåda',
    'admin.email.mb.sub':                    'Adressen agenter skickar från, och dit svar landar. Hanteras av HQ.',
    'admin.email.mb.change_address':         'Ändra adress',
    'admin.email.mb.address':                'Adress',
    'admin.email.mb.hint':                   'Endast gemener, siffror och . / -.',
    'admin.email.mb.updated':                'Arbetsytans brevlåda uppdaterad.',
    'admin.email.mb.err_shape':              'Det ser inte ut som en giltig adress.',
    'admin.email.mb.err_taken':              'Den adressen används redan.',
    'admin.email.mb.err_update':             'Det gick inte att uppdatera brevlådan.',
    'admin.email.mb.court.title':            'Domstolshandlingar (Sverige)',
    'admin.email.mb.court.sub':              'När en svensk domstol skickar handlingar via "Säker e-post" laddas de ner till biblioteket automatiskt. E-postmeddelandet visas fortfarande i din granskningskö.',
    'admin.email.mb.court.configure':        'Konfigurera',
    'admin.email.mb.court.on':               'På',
    'admin.email.mb.court.off':              'Av',
    'admin.email.mb.court.pw_set':           'Lösenord för säker e-post sparat',
    'admin.email.mb.court.enable':           'Ladda ner domstolshandlingar automatiskt',
    'admin.email.mb.court.password':         'Lösenord för säker e-post',
    'admin.email.mb.court.pw_ph':            'Lösenord till domstolens portal för säker e-post',
    'admin.email.mb.court.pw_keep':          'Lämna tomt för att behålla sparat lösenord',
    'admin.email.mb.court.hint':             'Används för att öppna domstolens "Säker e-post". Lagras krypterat och visas aldrig igen.',
    'admin.email.mb.court.saved':            'Inställningar för domstolshandlingar sparade.',
    'admin.email.mb.court.err':              'Det gick inte att spara inställningarna.',
    'admin.email.reply.agent_decides':       'Agenten avgör',
    'admin.email.reply.always':              'Svara alltid',
    'admin.email.reply.never':               'Svara aldrig',
    'admin.email.reply.human_review':        'Utkast för mänsklig granskning',
    'admin.email.sender.anyone':             'Vem som helst',
    'admin.email.sender.bound':              'Endast bundna användare',
    'admin.email.sender.domain':             'Domänlista',
    'admin.email.sender.addr':               'Adresslista',
    'admin.email.answered.files':            'Endast filer',
    'admin.email.answered.quick':            'Snabbsvar',
    'admin.email.state.handled':             'Hanterad',
    'admin.email.state.awaiting':            'Väntar på granskning',
    'admin.email.state.unrouted':            'Odirigerad',
    'admin.email.reason.tenant_unknown':     'Okänd arbetsyta',
    'admin.email.reason.alias_expired':      'Alias har gått ut',
    'admin.email.reason.agent_unknown':      'Okänd agent',
    'admin.email.reason.agent_disabled':     'Agent inaktiverad',
    'admin.email.reason.unknown_sender':     'Okänd avsändare',
    'admin.email.reason.spam':               'Skräppostgräns',
    'admin.email.reason.malformed':          'Felaktig adress',
    'admin.email.reason.category_blocked':   'Kategori blockerad',
    'admin.email.reason.rate_limited':       'Hastighetsbegränsad',
    'admin.email.sort.newest':               'Nyaste',
    'admin.email.sort.oldest':               'Äldsta',
    'admin.email.sort.title_az':             'Titel A-Ö',
    'admin.email.metric.received':           'mottagna',
    'admin.email.metric.replied':            'besvarade',
    'admin.email.metric.escalated':          'eskalerade',
    'admin.email.metric.awaiting':           'väntar',
    'admin.email.metric.unrouted':           'odirigerade',
    'admin.email.err.write_reply':           'Skriv ett svar först.',
    'admin.email.err.send':                  'Det gick inte att skicka svaret.',
    'admin.email.err.pick_agent':            'Välj en agent först.',
    'admin.email.err.assign':                'Det gick inte att tilldela meddelandet.',
    'admin.email.err.accept':                'Det gick inte att acceptera meddelandet.',

    // ── web-ui EmailMessageView.vue (admin.emailMessage.*) ──
    'admin.emailMessage.title_message':      'E-postmeddelande',
    'admin.emailMessage.title_quarantine':   'Meddelande i karantän',
    'admin.emailMessage.lens_text':          'Text',
    'admin.emailMessage.lens_html':          'HTML',
    'admin.emailMessage.reject':             'Avvisa',
    'admin.emailMessage.accept':             'Acceptera',
    'admin.emailMessage.assign_retry':       'Tilldela och försök igen',
    'admin.emailMessage.open_thread':        'Öppna tråd',
    'admin.emailMessage.discard':            'Förkasta',
    'admin.emailMessage.done':               'Klar',
    'admin.emailMessage.done_toast':         'Markerat som granskat.',
    'admin.emailMessage.reopen':             'Återöppna',
    'admin.emailMessage.reopen_toast':       'Flyttad tillbaka till granskning.',
    'admin.emailMessage.quar_notice':        'Hålls vid avsändargrinden. Att läsa detta släpper inte igenom det.',
    'admin.emailMessage.meta_date':          'Datum',
    'admin.emailMessage.meta_from':          'Från',
    'admin.emailMessage.meta_to':            'Till',
    'admin.emailMessage.meta_cc':            'Kopia',
    'admin.emailMessage.meta_subject':       'Ämne',
    'admin.emailMessage.body_format_aria':   'Brödtextformat',
    'admin.emailMessage.bind_label':         'Bind avsändare till en användare (valfritt)',
    'admin.emailMessage.choose_user':        'Välj en användare…',
    'admin.emailMessage.bind_aria':          'Bind avsändare till användare',
    'admin.emailMessage.assign_label':       'Tilldela till en agent',
    'admin.emailMessage.choose_agent':       'Välj en agent…',
    'admin.emailMessage.assign_aria':        'Tilldela till agent',
    'admin.emailMessage.html_body_title':    'E-postens HTML-brödtext',
    'admin.emailMessage.no_body':            'Det här meddelandet har ingen läsbar brödtext.',
    'admin.emailMessage.attachment_count_one':'{count} bilaga',
    'admin.emailMessage.attachment_count_other':'{count} bilagor',
    'admin.emailMessage.attachment_fallback':'bilaga',
    'admin.emailMessage.drag_resize':        'Dra för att ändra storlek',
    'admin.emailMessage.download':           'Ladda ner',
    'admin.emailMessage.close_preview':      'Stäng förhandsvisning',
    'admin.emailMessage.this_sender':        'den här avsändaren',
    'admin.emailMessage.accepted':           'Accepterad.',
    'admin.emailMessage.rejected':           'Avvisad.',
    'admin.emailMessage.draft_discarded':    'Utkastet förkastat.',
    'admin.emailMessage.assigned':           'Tilldelad - agenten hanterar det nu.',
    'admin.emailMessage.reject_title':       'Avvisa det här mejlet?',
    'admin.emailMessage.reject_body':        'E-post från {from} kommer att avfärdas. Detta tar inte bort den från granskningsloggen.',
    'admin.emailMessage.discard_title':      'Förkasta det här utkastet?',
    'admin.emailMessage.discard_body':       'Det utkastade svaret skickas inte. Tråden stannar i din historik.',
    'admin.emailMessage.err.load':           'Det gick inte att läsa in meddelandet.',
    'admin.emailMessage.err.generic':        'Något gick fel.',

    // ── web-ui EmailEndpointEditorView.vue (admin.emailEndpoint.*) ──
    'admin.emailEndpoint.title_new':         'Ny e-postslutpunkt',
    'admin.emailEndpoint.title_edit':        'Redigera {name}',
    'admin.emailEndpoint.endpoint_fallback': 'slutpunkt',
    'admin.emailEndpoint.enabled_aria':      'Inkorg aktiverad',
    'admin.emailEndpoint.delete_inbox':      'Ta bort inkorg',
    'admin.emailEndpoint.save_changes':      'Spara ändringar',
    'admin.emailEndpoint.create_endpoint':   'Skapa slutpunkt',
    'admin.emailEndpoint.not_found':         'Slutpunkten hittades inte.',
    'admin.emailEndpoint.plane_light':       'Snabbsvar',
    'admin.emailEndpoint.plane_agent':       'Agent',
    'admin.emailEndpoint.sender_bound':      'Endast bundna användare',
    'admin.emailEndpoint.sender_domain':     'Domänlista',
    'admin.emailEndpoint.sender_addr':       'Adresslista',
    'admin.emailEndpoint.sender_anyone':     'Vem som helst (offentlig)',
    'admin.emailEndpoint.reply_agent_decides':'Agenten avgör',
    'admin.emailEndpoint.reply_always':      'Svara alltid',
    'admin.emailEndpoint.reply_never':       'Svara aldrig',
    'admin.emailEndpoint.reply_human_review':'Utkast för mänsklig granskning',
    'admin.emailEndpoint.address.title':     'Adress',
    'admin.emailEndpoint.address.lede':      'Mejladressen dina agenter svarar på. Anges en gång, sedan fast.',
    'admin.emailEndpoint.address.label':     'Adress',
    'admin.emailEndpoint.address.hint':      "namnet före bindestrecket; gemener, siffror, '.' / '-'",
    'admin.emailEndpoint.address.err_no_email':'E-post är inte uppsatt för den här arbetsytan än - en administratör behöver ange dess e-postadress innan slutpunkter läggs till.',
    'admin.emailEndpoint.address.err_taken': 'Den adressen används redan - välj ett annat namn.',
    'admin.emailEndpoint.address.full':      'Fullständig adress:',
    'admin.emailEndpoint.address.fixed':     'Adressen är fast efter att den skapats.',
    'admin.emailEndpoint.replies.title':     'Svar',
    'admin.emailEndpoint.replies.lede':      'Om den här inkorgen besvarar e-post över huvud taget.',
    'admin.emailEndpoint.replies.when_label':'När den ska svara',
    'admin.emailEndpoint.replies.never_hint':'Den här inkorgen svarar aldrig - e-post tas emot, kategoriseras och arkiveras bara. Ingen som svarar behövs.',
    'admin.emailEndpoint.who.title':         'Vem som svarar',
    'admin.emailEndpoint.who.lede':          'Ett snabbsvar besvaras av HQ direkt (ingen agent). Välj Agent för att lämna över tråden till en specifik agent istället.',
    'admin.emailEndpoint.who.mode_label':    'Svarsläge',
    'admin.emailEndpoint.who.mode_hint_light':'Snabbsvar: HQ skriver ett snabbt svar med minne + dokumentåterkallning - ingen agent, ingen sandbox. Debiteras + mäts.',
    'admin.emailEndpoint.who.mode_hint_agent':'Agent: en specifik agent hanterar tråden med hela sin verktygsuppsättning (filer, webbläsare, integrationer) i en sandbox.',
    'admin.emailEndpoint.who.agent_label':   'Agent',
    'admin.emailEndpoint.who.agent_hint_none':'Inga aktiverade agenter - skapa en först.',
    'admin.emailEndpoint.who.agent_hint':    'Agenten behöver E-post-integrationen aktiverad för att skicka svar.',
    'admin.emailEndpoint.who.agent_placeholder':'Välj en agent…',
    'admin.emailEndpoint.who.instructions_label':'Instruktioner',
    'admin.emailEndpoint.who.instructions_hint':'rollanteckningar för den här adressen (valfritt)',
    'admin.emailEndpoint.who.instructions_placeholder':'Du hanterar inkommande säljmejl för ACME. Var kortfattad; ange aldrig priser utan att bekräfta.',
    'admin.emailEndpoint.who.reply_lang_label':'Svarsspråk',
    'admin.emailEndpoint.who.reply_lang_hint':'svar matchar avsändarens språk automatiskt; detta är bara reserven för när det inte kan avgöras',
    'admin.emailEndpoint.who.reply_lang_placeholder':'Engelska (reserv)',
    'admin.emailEndpoint.access.title':      'Vem som får skriva in',
    'admin.emailEndpoint.access.lede':       'Avsändargrinden. E-post från avsändare utanför den sätts i karantän.',
    'admin.emailEndpoint.access.sender_label':'Avsändarpolicy',
    'admin.emailEndpoint.access.allowlist_label':'Tillåtelselista',
    'admin.emailEndpoint.access.allowlist_hint_domains':'domäner (Enter för att lägga till)',
    'admin.emailEndpoint.access.allowlist_hint_addresses':'adresser (Enter för att lägga till)',
    'admin.emailEndpoint.limits.title':      'Gränser',
    'admin.emailEndpoint.limits.lede':       'Kostnads- / missbruksbrytare. Tomt = plattformens standard.',
    'admin.emailEndpoint.limits.spam_label': 'Högsta skräppostpoäng',
    'admin.emailEndpoint.limits.turns_sender_label':'Turer / avsändare / dag',
    'admin.emailEndpoint.limits.turns_day_label':'Turer / dag',
    'admin.emailEndpoint.updated':           'Slutpunkten uppdaterad.',
    'admin.emailEndpoint.created':           'Slutpunkten skapad.',
    'admin.emailEndpoint.deleted':           'Slutpunkten borttagen.',
    'admin.emailEndpoint.delete_title':      'Ta bort den här slutpunkten?',
    'admin.emailEndpoint.delete_body':       'E-post till {address} kommer inte längre att besvaras. Befintliga trådar behålls.',
    'admin.emailEndpoint.err.save':          'Det gick inte att spara slutpunkten.',
  },
};
